import { Grid, TableCell, TableRow, Typography } from "@mui/material";
import { Flex } from "~/components/Flex";
import { IntegrationConfigurationsAndMessagesHalfDataTable } from "./IntegrationConfigurationsAndMessagesHalfDataTable";
import { HostedClientIntegration } from "../../utils/integrationsTypes";
import { useIntegrationConfigurationsAndMessages } from "~/pages/integrations/components/configurations-and-messages/hooks/useIntegrationConfigurationsAndMessages";
import { IntegrationConfigurationsAndMessagesHalfDataTableRow } from "./IntegrationConfigurationsAndMessagesHalfDataTableRow";
import { FormatDateShort } from "~/lib/date";
import { capitalizeFirstLetter } from "~/lib/helpers";
import { TICKET_SYSTEM_INTEGRATIONS } from "~/pages/integrations/hosted-integrations";

export interface IntegrationConfigurationsAndMessagesProps {
  integration: HostedClientIntegration;
  token?: string;
  actionType: "scan" | "export" | "import";
}

export function IntegrationConfigurationsAndMessages({
  integration,
  token,
  actionType,
}: IntegrationConfigurationsAndMessagesProps) {
  const { displayedConfigs, configFormat } =
    useIntegrationConfigurationsAndMessages({
      integration,
      token,
      actionType,
    });

  return (
    <Grid
      container
      columnSpacing={{ xs: 0, sm: 3 }}
      rowSpacing={{ xs: 5, sm: 0 }}
    >
      <Grid
        size={{
          xs: 12,
          sm: 6,
        }}
      >
        <IntegrationConfigurationsAndMessagesHalfDataTable title="Integration Configuration">
          <IntegrationConfigurationsAndMessagesHalfDataTableRow
            title="Created"
            value={FormatDateShort(integration.created)}
          />

          <IntegrationConfigurationsAndMessagesHalfDataTableRow
            title="Last modified"
            value={
              integration?.lastModified
                ? FormatDateShort(integration?.lastModified)
                : "Never"
            }
          />

          {displayedConfigs &&
            Object.entries(displayedConfigs).map(([key, value]) => (
              <IntegrationConfigurationsAndMessagesHalfDataTableRow
                key={key}
                title={configFormat.configName(key)}
                value={
                  (configFormat.group.includes(key) ? (
                    <>{configFormat.value(value)}</>
                  ) : (
                    <>{value}</>
                  )) || <>&nbsp;</>
                }
              />
            ))}
        </IntegrationConfigurationsAndMessagesHalfDataTable>
        {!TICKET_SYSTEM_INTEGRATIONS.includes(integration.type) && (
          <IntegrationConfigurationsAndMessagesHalfDataTable
            title={`${actionType} Details`}
            ContainerSxProps={{ mt: 3 }}
          >
            <IntegrationConfigurationsAndMessagesHalfDataTableRow
              title={`Last Successful ${capitalizeFirstLetter(actionType)}`}
              value={configFormat.scanStartAndFinish(
                integration?.scanJobStarted,
                integration?.scanJobFinished,
              )}
            />
            <IntegrationConfigurationsAndMessagesHalfDataTableRow
              title={`Last Attempted ${capitalizeFirstLetter(actionType)}`}
              value={
                integration?.scanJobAttempted
                  ? FormatDateShort(integration.scanJobAttempted)
                  : "Never"
              }
            />
          </IntegrationConfigurationsAndMessagesHalfDataTable>
        )}
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
        }}
      >
        <IntegrationConfigurationsAndMessagesHalfDataTable title="Most Recent Activity">
          {integration.messages && integration.messages.length > 0 ? (
            integration.messages.map((message, i) => (
              <IntegrationConfigurationsAndMessagesHalfDataTableRow
                key={i}
                title={
                  message?.timestamp
                    ? FormatDateShort(message.timestamp)
                    : "Never"
                }
                value={message.message}
              />
            ))
          ) : (
            <TableRow>
              <TableCell align="center">
                <Flex
                  sx={{
                    height: (theme) => theme.spacing(10),
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="text.secondary">
                    You have no recent activity.
                  </Typography>
                </Flex>
              </TableCell>
            </TableRow>
          )}
        </IntegrationConfigurationsAndMessagesHalfDataTable>
      </Grid>
    </Grid>
  );
}
